/**
 * SupplierPayablesPanel
 * ---------------------
 * The money going out: the purchase order we issue a supplier or
 * subcontractor, the invoice they bill us, and every payment made against it
 * with the wire / ACH confirmation on file. Shared by the Sourcing Desk order
 * drawer (link_type "backorder") and the Project Management window
 * (link_type "project").
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Plus, Trash2, Upload, FileText, Banknote, Mail, Download,
  ReceiptText, ClipboardList, CheckCircle2, Paperclip, Save,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API } from "@/App";
import SupplierPaymentDialog from "./SupplierPaymentDialog";
import RemittanceComposer from "./RemittanceComposer";

const money = (v) => `$${(Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PO_STATUS = ["draft", "issued", "received", "closed", "cancelled"];
const INV_STATUS = ["received", "approved", "partially_paid", "paid", "disputed", "void"];

const STATUS_CLS = {
  draft: "bg-slate-100 text-slate-700",
  issued: "bg-blue-50 text-blue-700",
  received: "bg-amber-50 text-amber-700",
  approved: "bg-indigo-50 text-indigo-700",
  partially_paid: "bg-orange-50 text-orange-700",
  paid: "bg-emerald-50 text-emerald-700",
  closed: "bg-emerald-50 text-emerald-700",
  disputed: "bg-red-50 text-red-700",
  cancelled: "bg-red-50 text-red-700",
  void: "bg-red-50 text-red-700",
};

const METHOD_LABEL = {
  wire: "Commercial wire", ach: "ACH", check: "Check", card: "Card",
  qbo_bill_pay: "QBO bill pay", other: "Other",
};

export const SupplierPayablesPanel = ({ linkType, linkId, token, defaultSupplier }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState("");
  const [payOpen, setPayOpen] = useState(false);
  const [remitFor, setRemitFor] = useState(null);
  const uploadRef = useRef({ kind: "", id: "", docType: "" });
  const fileInput = useRef(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/payables/ledger`, {
        params: { link_type: linkType, link_id: linkId },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load the payables ledger");
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkType, linkId, token]);

  useEffect(() => { load(); }, [load]);

  const pos = data?.purchase_orders || [];
  const invoices = data?.invoices || [];
  const payments = data?.payments || [];
  const totals = data?.totals || {};

  // ------------------------------------------------------------- mutations
  const addPo = async () => {
    const name = window.prompt("Supplier / subcontractor name",
      defaultSupplier?.name || "") || "";
    if (!name.trim()) return;
    try {
      await axios.post(`${API}/payables/pos`, {
        link_type: linkType, link_id: linkId, supplier_name: name.trim(),
        supplier_email: (defaultSupplier?.email || "").toLowerCase() || null,
        status: "draft", amount: 0,
      }, { headers });
      toast.success("Purchase order record added");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add that PO"); }
  };

  const savePo = async (po, patch) => {
    setSavingId(po.id);
    try {
      await axios.put(`${API}/payables/pos/${po.id}`, patch, { headers });
      toast.success("PO saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save that PO"); }
    finally { setSavingId(""); }
  };

  const removeRecord = async (kind, id, label) => {
    if (!window.confirm(`Remove ${label}?`)) return;
    try {
      await axios.delete(`${API}/payables/${kind}/${id}`, { headers });
      toast.success("Removed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove that record"); }
  };

  const addInvoice = async (po) => {
    const number = window.prompt("Supplier invoice number") || "";
    if (!number.trim()) return;
    const amount = window.prompt(`Invoice amount for #${number.trim()}`, "") || "";
    try {
      await axios.post(`${API}/payables/invoices`, {
        link_type: linkType, link_id: linkId, po_id: po?.id || null,
        supplier_name: po?.supplier_name || defaultSupplier?.name || "Supplier",
        supplier_email: po?.supplier_email || defaultSupplier?.email || null,
        invoice_number: number.trim(), amount: Number(amount) || 0,
        invoice_date: new Date().toISOString().slice(0, 10), status: "received",
      }, { headers });
      toast.success("Invoice recorded");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not record that invoice"); }
  };

  const saveInvoice = async (inv, patch) => {
    setSavingId(inv.id);
    try {
      await axios.put(`${API}/payables/invoices/${inv.id}`, patch, { headers });
      toast.success("Invoice saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save that invoice"); }
    finally { setSavingId(""); }
  };

  const pickFile = (kind, id, docType) => {
    uploadRef.current = { kind, id, docType };
    fileInput.current?.click();
  };

  const doUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const { kind, id, docType } = uploadRef.current;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", docType);
    try {
      await axios.post(`${API}/payables/${kind}/${id}/attachments`, fd, { headers });
      toast.success(`${file.name} attached`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
  };

  const dropAttachment = async (kind, id, attId) => {
    try {
      await axios.delete(`${API}/payables/${kind}/${id}/attachments/${attId}`, { headers });
      load();
    } catch { toast.error("Could not remove that file"); }
  };

  const exportCsv = async () => {
    try {
      const r = await axios.get(`${API}/payables/qbo-export`, {
        params: { link_type: linkType, link_id: linkId },
        headers, responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `supplier-payables-${linkId}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not build the export"); }
  };

  if (loading) {
    return <p className="text-slate-500 inline-flex items-center gap-2 py-6">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading payables…</p>;
  }

  return (
    <div className="space-y-5" data-testid="supplier-payables-panel">
      <input type="file" ref={fileInput} onChange={doUpload} className="hidden" />

      {/* Roll-up */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Committed on POs" value={money(totals.po_total)} icon={ClipboardList} tone="text-blue-700 bg-blue-50" />
        <Stat label="Invoiced to us" value={money(totals.invoiced_total)} icon={ReceiptText} tone="text-amber-700 bg-amber-50" />
        <Stat label="Paid" value={money(totals.paid_total)} icon={CheckCircle2} tone="text-emerald-700 bg-emerald-50" />
        <Stat label="Balance owed" value={money(totals.balance)} icon={Banknote} tone="text-slate-900 bg-slate-100" testId="payables-balance" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={addPo} data-testid="payables-add-po">
          <Plus className="w-3.5 h-3.5 mr-1" /> Supplier PO
        </Button>
        <Button size="sm" variant="outline" onClick={() => addInvoice(null)} data-testid="payables-add-invoice">
          <Plus className="w-3.5 h-3.5 mr-1" /> Supplier invoice
        </Button>
        <Button size="sm" onClick={() => setPayOpen(true)}
          className="bg-emerald-600 hover:bg-emerald-700" data-testid="payables-record-payment">
          <Banknote className="w-3.5 h-3.5 mr-1" /> Record payment
        </Button>
        <Button size="sm" variant="ghost" onClick={exportCsv}
          className="text-slate-600" data-testid="payables-export">
          <Download className="w-3.5 h-3.5 mr-1" /> QBO reconciliation CSV
        </Button>
      </div>

      {/* Per-supplier roll-up */}
      {(data?.suppliers || []).length > 0 && (
        <Section title="By supplier">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[620px] text-sm">
              <thead className="bg-slate-50">
                <tr>{["Supplier", "PO'd", "Invoiced", "Paid", "Balance"].map((h) => (
                  <th key={h} className="text-left px-3 py-2 text-[10px] font-black uppercase tracking-wider text-slate-500">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.suppliers.map((s) => (
                  <tr key={s.key} data-testid={`payables-supplier-${s.key}`}>
                    <td className="px-3 py-2">
                      <p className="font-semibold text-slate-900">{s.supplier_name}</p>
                      <p className="text-[11px] text-slate-500">{s.supplier_email || "no email on file"}</p>
                    </td>
                    <td className="px-3 py-2 tabular-nums">{money(s.po_total)}</td>
                    <td className="px-3 py-2 tabular-nums">{money(s.invoiced_total)}</td>
                    <td className="px-3 py-2 tabular-nums text-emerald-700">{money(s.paid_total)}</td>
                    <td className="px-3 py-2 tabular-nums font-bold">{money(s.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* Purchase orders */}
      <Section title={`Purchase orders issued (${pos.length})`}>
        {pos.length === 0 ? (
          <Empty text="No supplier PO on file. One is drafted automatically when you award a supplier." />
        ) : pos.map((po) => (
          <PoRow key={`${po.id}-${po.status}-${po.amount}-${po.po_number}`} po={po} saving={savingId === po.id}
            onSave={(patch) => savePo(po, patch)}
            onDelete={() => removeRecord("pos", po.id, `PO for ${po.supplier_name}`)}
            onUpload={() => pickFile("pos", po.id, "purchase_order")}
            onDropFile={(attId) => dropAttachment("pos", po.id, attId)}
            onAddInvoice={() => addInvoice(po)} />
        ))}
      </Section>

      {/* Invoices */}
      <Section title={`Supplier invoices (${invoices.length})`}>
        {invoices.length === 0 ? <Empty text="No supplier invoices keyed yet." /> : invoices.map((inv) => (
          <InvoiceRow key={`${inv.id}-${inv.status}-${inv.amount}-${inv.paid_total}`} inv={inv} pos={pos} saving={savingId === inv.id}
            onSave={(patch) => saveInvoice(inv, patch)}
            onDelete={() => removeRecord("invoices", inv.id, `invoice #${inv.invoice_number}`)}
            onUpload={() => pickFile("invoices", inv.id, "supplier_invoice")}
            onDropFile={(attId) => dropAttachment("invoices", inv.id, attId)} />
        ))}
      </Section>

      {/* Payments */}
      <Section title={`Payments made (${payments.length})`}>
        {payments.length === 0 ? <Empty text="No payments recorded." /> : payments.map((p) => (
          <div key={p.id} className="border border-slate-200 rounded-lg p-3 mb-2"
            data-testid={`payment-row-${p.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="font-bold text-slate-900">
                  {money(p.amount)} · {METHOD_LABEL[p.method] || p.method}
                  {p.notified_at && (
                    <span className="ml-2 text-[10px] font-black uppercase tracking-wider text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
                      remittance sent
                    </span>
                  )}
                </p>
                <p className="text-[11px] text-slate-500">
                  {p.supplier_name} · {p.payment_date}
                  {p.reference ? ` · ref ${p.reference}` : ""}
                  {p.bank_name ? ` · ${p.bank_name}` : ""}
                  {p.account_last4 ? ` · acct ending ${p.account_last4}` : ""}
                </p>
                <ul className="mt-1 text-[11px] text-slate-600">
                  {(p.allocations || []).map((a, i) => (
                    <li key={i}>Invoice #{a.invoice_number} — <strong>{money(a.amount)}</strong></li>
                  ))}
                </ul>
                <Files atts={p.attachments} onDrop={(id) => dropAttachment("payments", p.id, id)} />
              </div>
              <div className="flex flex-wrap gap-1.5">
                <Button size="sm" variant="outline" onClick={() => pickFile("payments", p.id, "wire_confirmation")}
                  data-testid={`payment-upload-${p.id}`}>
                  <Upload className="w-3.5 h-3.5 mr-1" /> Confirmation
                </Button>
                <Button size="sm" onClick={() => setRemitFor(p)}
                  className="bg-blue-600 hover:bg-blue-700" data-testid={`payment-remit-${p.id}`}>
                  <Mail className="w-3.5 h-3.5 mr-1" /> Notify supplier
                </Button>
                <Button size="sm" variant="ghost" onClick={() => removeRecord("payments", p.id, `payment of ${money(p.amount)}`)}
                  className="text-red-600 hover:bg-red-50">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </Section>

      <SupplierPaymentDialog open={payOpen} onClose={() => setPayOpen(false)} token={token}
        linkType={linkType} linkId={linkId} invoices={invoices}
        onSaved={(pay) => { load(); setRemitFor(pay); }} />

      <RemittanceComposer open={!!remitFor} onClose={() => setRemitFor(null)} token={token}
        payment={remitFor} onSent={load} />
    </div>
  );
};

// ------------------------------------------------------------------ pieces
const Stat = ({ label, value, icon: Icon, tone, testId }) => (
  <div className="border border-slate-200 rounded-lg p-3" data-testid={testId}>
    <div className={`w-7 h-7 rounded-md inline-flex items-center justify-center mb-1.5 ${tone}`}>
      <Icon className="w-4 h-4" />
    </div>
    <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">{label}</p>
    <p className="text-lg font-black text-slate-900 tabular-nums">{value}</p>
  </div>
);

const Section = ({ title, children }) => (
  <section>
    <h4 className="text-[11px] font-black uppercase tracking-wider text-slate-500 mb-2">{title}</h4>
    {children}
  </section>
);

const Empty = ({ text }) => (
  <p className="text-sm text-slate-500 border border-dashed border-slate-300 rounded-lg p-4">{text}</p>
);

const Pill = ({ status }) => (
  <span className={`text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded ${STATUS_CLS[status] || "bg-slate-100 text-slate-700"}`}>
    {(status || "").replace(/_/g, " ")}
  </span>
);

const Files = ({ atts, onDrop }) => (
  (atts || []).length ? (
    <div className="mt-1.5 flex flex-wrap gap-1.5">
      {atts.map((a) => (
        <span key={a.id} className="inline-flex items-center gap-1 text-[11px] bg-slate-100 rounded px-1.5 py-0.5">
          <Paperclip className="w-3 h-3 text-slate-500" />
          <a href={a.url} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline">{a.name}</a>
          <button onClick={() => onDrop(a.id)} className="text-slate-400 hover:text-red-600">×</button>
        </span>
      ))}
    </div>
  ) : null
);

const PoRow = ({ po, saving, onSave, onDelete, onUpload, onDropFile, onAddInvoice }) => {
  const [f, setF] = useState({
    po_number: po.po_number || "", issue_date: po.issue_date || "", need_by: po.need_by || "",
    terms: po.terms || "", amount: po.amount ?? 0, status: po.status || "draft",
  });
  return (
    <div className="border border-slate-200 rounded-lg p-3 mb-2" data-testid={`po-row-${po.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div>
          <p className="font-bold text-slate-900 inline-flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-600" /> {po.supplier_name} <Pill status={po.status} />
          </p>
          <p className="text-[11px] text-slate-500">{po.supplier_email || "no email on file"}
            {po.auto_drafted ? " · auto-drafted on award" : ""}</p>
        </div>
        <div className="flex gap-1.5">
          <Button size="sm" variant="outline" onClick={onUpload}>
            <Upload className="w-3.5 h-3.5 mr-1" /> PO copy
          </Button>
          <Button size="sm" variant="outline" onClick={onAddInvoice} data-testid={`po-add-invoice-${po.id}`}>
            <ReceiptText className="w-3.5 h-3.5 mr-1" /> Log invoice
          </Button>
          <Button size="sm" variant="ghost" onClick={onDelete} className="text-red-600 hover:bg-red-50">
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-2">
        <Cell label="PO number">
          <Input value={f.po_number} onChange={(e) => setF({ ...f, po_number: e.target.value })}
            className="h-8 text-xs" data-testid={`po-number-${po.id}`} />
        </Cell>
        <Cell label="Issued">
          <Input type="date" value={f.issue_date} onChange={(e) => setF({ ...f, issue_date: e.target.value })} className="h-8 text-xs" />
        </Cell>
        <Cell label="Need by">
          <Input type="date" value={f.need_by} onChange={(e) => setF({ ...f, need_by: e.target.value })} className="h-8 text-xs" />
        </Cell>
        <Cell label="Terms">
          <Input value={f.terms} onChange={(e) => setF({ ...f, terms: e.target.value })}
            className="h-8 text-xs" placeholder="Net 30" />
        </Cell>
        <Cell label="Amount">
          <Input type="number" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })}
            className="h-8 text-xs text-right font-semibold" data-testid={`po-amount-${po.id}`} />
        </Cell>
        <Cell label="Status">
          <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}
            className="w-full h-8 text-xs border border-slate-300 rounded-md px-1" data-testid={`po-status-${po.id}`}>
            {PO_STATUS.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
        </Cell>
      </div>
      <Files atts={po.attachments} onDrop={onDropFile} />
      <div className="mt-2 text-right">
        <Button size="sm" onClick={() => onSave({ ...f, amount: Number(f.amount) || 0 })} disabled={saving}
          className="bg-slate-900 hover:bg-slate-800" data-testid={`po-save-${po.id}`}>
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save PO
        </Button>
      </div>
    </div>
  );
};

const InvoiceRow = ({ inv, pos, saving, onSave, onDelete, onUpload, onDropFile }) => {
  const [f, setF] = useState({
    invoice_number: inv.invoice_number || "", invoice_date: inv.invoice_date || "",
    due_date: inv.due_date || "", amount: inv.amount ?? 0, status: inv.status || "received",
    po_id: inv.po_id || "",
  });
  return (
    <div className="border border-slate-200 rounded-lg p-3 mb-2" data-testid={`invoice-row-${inv.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div>
          <p className="font-bold text-slate-900 inline-flex items-center gap-2">
            <ReceiptText className="w-4 h-4 text-amber-600" /> Invoice #{inv.invoice_number}
            <Pill status={inv.status} />
          </p>
          <p className="text-[11px] text-slate-500">
            {inv.supplier_name} · paid {money(inv.paid_total)} of {money(inv.amount)} ·
            <strong className="text-slate-700"> open {money(inv.balance)}</strong>
            {inv.approved_by ? ` · approved by ${inv.approved_by}` : ""}
          </p>
        </div>
        <div className="flex gap-1.5">
          <Button size="sm" variant="outline" onClick={onUpload} data-testid={`invoice-upload-${inv.id}`}>
            <Upload className="w-3.5 h-3.5 mr-1" /> Invoice file
          </Button>
          <Button size="sm" variant="ghost" onClick={onDelete} className="text-red-600 hover:bg-red-50">
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-2">
        <Cell label="Invoice #">
          <Input value={f.invoice_number} onChange={(e) => setF({ ...f, invoice_number: e.target.value })} className="h-8 text-xs" />
        </Cell>
        <Cell label="Invoice date">
          <Input type="date" value={f.invoice_date} onChange={(e) => setF({ ...f, invoice_date: e.target.value })} className="h-8 text-xs" />
        </Cell>
        <Cell label="Due date">
          <Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} className="h-8 text-xs" />
        </Cell>
        <Cell label="Against PO">
          <select value={f.po_id} onChange={(e) => setF({ ...f, po_id: e.target.value })}
            className="w-full h-8 text-xs border border-slate-300 rounded-md px-1">
            <option value="">— none —</option>
            {pos.map((p) => <option key={p.id} value={p.id}>{p.po_number || "(no number)"} · {p.supplier_name}</option>)}
          </select>
        </Cell>
        <Cell label="Amount">
          <Input type="number" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })}
            className="h-8 text-xs text-right font-semibold" data-testid={`invoice-amount-${inv.id}`} />
        </Cell>
        <Cell label="Status">
          <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}
            className="w-full h-8 text-xs border border-slate-300 rounded-md px-1" data-testid={`invoice-status-${inv.id}`}>
            {INV_STATUS.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
        </Cell>
      </div>
      <Files atts={inv.attachments} onDrop={onDropFile} />
      <div className="mt-2 flex justify-end gap-2">
        {!inv.approved_by && (
          <Button size="sm" variant="outline" onClick={() => onSave({ approved: true })}
            className="border-indigo-300 text-indigo-700" data-testid={`invoice-approve-${inv.id}`}>
            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve for payment
          </Button>
        )}
        <Button size="sm" onClick={() => onSave({ ...f, amount: Number(f.amount) || 0, po_id: f.po_id || null })}
          disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid={`invoice-save-${inv.id}`}>
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save invoice
        </Button>
      </div>
    </div>
  );
};

const Cell = ({ label, children }) => (
  <label className="block">
    <span className="text-[9px] font-black uppercase tracking-wider text-slate-500 block mb-0.5">{label}</span>
    {children}
  </label>
);

export default SupplierPayablesPanel;
